import { styled } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import { Avatar } from "@mui/material";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import React from "react";
import ListItemButton from "@mui/material/ListItemButton";
import { Link } from "react-router-dom";
import ListItemIcon from "@mui/material/ListItemIcon";
import HomeIcon from "@mui/icons-material/Home";
import ListItemText from "@mui/material/ListItemText";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import ArticleIcon from "@mui/icons-material/Article";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AddHomeWorkIcon from "@mui/icons-material/AddHomeWork";
import LogoutIcon from "@mui/icons-material/Logout";
import AssessmentIcon from "@mui/icons-material/Assessment";
import useRole from "../../../hooks/useRole";
import Roles from "../../../helpers/roles";

const drawerWidth = 240;

const drawerPaperStyles = (theme) => ({
    width: drawerWidth,
    boxSizing: 'border-box',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.secondary.main,
    display: 'flex',
    flexDirection: 'column',
    '& .MuiListItemIcon-root, .MuiIconButton-root': {
        color: 'inherit',
    },
});

const PermanentDrawer = styled(MuiDrawer)(({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    '& .MuiDrawer-paper': drawerPaperStyles(theme),
}));

const TemporaryDrawer = styled(MuiDrawer)(({ theme }) => ({
    '& .MuiDrawer-paper': drawerPaperStyles(theme),
}));

const MainDrawer = ({ mobileOpen = false, onClose, onLogout }) => {
    const { role } = useRole();

    const handleNav = () => {
        if (onClose) onClose();
    };

    const drawerContent = (
        <>
            {/* Header: logo + app name */}
            <Box
                component={Link}
                to={'/'}
                onClick={handleNav}
                sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 3,
                }}
            >
                <Avatar
                    src='/logo_cabana.png'
                    alt='La Cabaña'
                    sx={{
                        width: 100,
                        height: 100,
                        bgcolor: 'white',
                        mb: 1.5,
                    }}
                />
                <Typography
                    variant='h6'
                    sx={{
                        color: 'white',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                    }}
                >
                    La Cabaña
                </Typography>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

            {/* Menu items */}
            <List component="nav" sx={{ flexGrow: 1, py: 1 }}>
                <ListItemButton component={Link} to={'/'} onClick={handleNav}>
                    <ListItemIcon>
                        <HomeIcon />
                    </ListItemIcon>
                    <ListItemText primary="Inicio" />
                </ListItemButton>

                {role === Roles.Admin && (
                    <ListItemButton component={Link} to={'/references'} onClick={handleNav}>
                        <ListItemIcon>
                            <BusinessCenterIcon />
                        </ListItemIcon>
                        <ListItemText primary="Referencias" />
                    </ListItemButton>
                )}

                <ListItemButton component={Link} to={'/invoices'} onClick={handleNav}>
                    <ListItemIcon>
                        <ArticleIcon />
                    </ListItemIcon>
                    <ListItemText primary="Remisiones" />
                </ListItemButton>

                {role === Roles.Admin && (
                    <>
                        <ListItemButton component={Link} to={'/settles'} onClick={handleNav}>
                            <ListItemIcon>
                                <AssessmentIcon />
                            </ListItemIcon>
                            <ListItemText primary="Liquidaciones" />
                        </ListItemButton>
                        <ListItemButton component={Link} to={'/users'} onClick={handleNav}>
                            <ListItemIcon>
                                <PeopleAltIcon />
                            </ListItemIcon>
                            <ListItemText primary="Usuarios" />
                        </ListItemButton>
                        <ListItemButton component={Link} to={'/masters'} onClick={handleNav}>
                            <ListItemIcon>
                                <AddHomeWorkIcon />
                            </ListItemIcon>
                            <ListItemText primary="Maestros" />
                        </ListItemButton>
                    </>
                )}
            </List>

            {/* Logout pinned to bottom */}
            <Box>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
                <List component="nav" sx={{ py: 1 }}>
                    <ListItemButton onClick={onLogout}>
                        <ListItemIcon>
                            <LogoutIcon />
                        </ListItemIcon>
                        <ListItemText primary="Salir" />
                    </ListItemButton>
                </List>
            </Box>
        </>
    );

    return (
        <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
            {/* Mobile: temporary drawer overlays on top of content */}
            <TemporaryDrawer
                variant="temporary"
                open={mobileOpen}
                onClose={onClose}
                ModalProps={{ keepMounted: true }}
                sx={{ display: { xs: 'block', sm: 'none' } }}
            >
                {drawerContent}
            </TemporaryDrawer>

            {/* Tablet+ desktop: permanent drawer always visible */}
            <PermanentDrawer
                variant="permanent"
                open
                sx={{ display: { xs: 'none', sm: 'block' } }}
            >
                {drawerContent}
            </PermanentDrawer>
        </Box>
    );
};

export default MainDrawer;
